import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import FormModal from '../../components/Modals/FormModal';
import InputField from '../../components/Forms/Input/InputField';
import TextAreaField from '../../components/Forms/Input/TextAreaField';
import SelectField from '../../components/Forms/Input/SelectField';
import { useDocumentType } from '../../hooks/useDocumentType';
import { useDocumentTemplate } from '../../hooks/documentTemplate/useDocumentTemplate';
import { useAllowedOptions } from '../../hooks/settings/useAllowedOptions';
import {
  updateTemplateField,
  closeRegistrationModalAndReset,
} from '../../store/slices/documentTemplate/electronicDocumentTemplateSlice';
import FileUploader from '../Uploader/FileUploader';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (file: File | null) => void; // ✅ nueva prop para guardar
}

const DocumentTemplateRegistrationPopup: React.FC<Props> = ({
  isOpen,
  onSave,
}) => {
  const dispatch = useDispatch();
  const { form } = useDocumentTemplate();
  const { fetchDocumentTypeDropdown, documentTypeDropdown } = useDocumentType();
  const { fetchCodesByModuleAndTarget, getCodes } = useAllowedOptions();

  // 🔹 Estado local para el archivo HTML (plantilla)
  const [templateFile, setTemplateFile] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchDocumentTypeDropdown();
      fetchCodesByModuleAndTarget('DTE_TEMPLATES', 'DOCUMENT_TEMPLATE');
      setTemplateFile(null);
    }
  }, [isOpen]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    // 🔄 Convertir a número si es documentTypeId
    const parsedValue = name === 'documentTypeId' ? Number(value) : value;

    dispatch(updateTemplateField({ field: name as any, value: parsedValue }));
  };

  const allowedCodes = getCodes('DTE_TEMPLATES', 'DOCUMENT_TEMPLATE');
  const filteredDocumentTypes = documentTypeDropdown.filter((d) =>
    allowedCodes.includes(d.code)
  );

  return (
    <FormModal
      isOpen={isOpen}
      onClose={() => dispatch(closeRegistrationModalAndReset())}
      title="Registrar Plantilla de Documento Electrónico"
      maxWidth="max-w-4xl"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(templateFile); // ✔️ se mantiene igual
        }}
      >
        {/* Tipo de Documento */}
        <SelectField
          label="Tipo de Documento"
          name="documentTypeId" // ✅ nombre en inglés
          iconKey="document"
          value={form.documentTypeId}
          onChange={handleChange}
          options={filteredDocumentTypes.map((item) => ({
            label: item.name,
            value: item.id,
          }))}
          placeholder="Seleccione"
        />

        {/* Versión */}
        <InputField
          label="Versión"
          name="version" // ✅ nombre en inglés
          iconKey="code"
          value={form.version || ''}
          onChange={handleChange}
          type="text"
        />

        {/* Descripción */}
        <TextAreaField
          label="Descripción"
          name="description" // ✅ nombre en inglés
          value={form.description || ''}
          onChange={handleChange}
          placeholder="Ingrese una descripción de la plantilla"
        />

        {/* Carga de archivo HTML */}
        <div className="md:col-span-2">
          <FileUploader
            label="Archivo de Plantilla HTML"
            accept="text/html"
            currentFile={templateFile}
            onFileChange={setTemplateFile}
          />
        </div>

        {/* Botón */}
        <div className="md:col-span-2">
          <button
            type="submit"
            className="w-full rounded bg-primary px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
          >
            Guardar Plantilla
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default DocumentTemplateRegistrationPopup;
